import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, FileText, Mail, MessageCircle, Radar, PlayCircle,
  ArrowRight, RotateCcw, LayoutDashboard, Share2, Check, Copy,
} from "lucide-react";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import {
  trackTourOpened, trackTourTabSwitched, trackTourClipProgress,
  trackTourCompleted, trackTourCtaClick,
} from "@/lib/landingAnalytics";

/**
 * ProductTourModal — full-screen dialog that plays the 4 in-app tour
 * clips (CV Builder, Motivation Letter, Interview Prep, Match Radar).
 *
 * Enhancements over the original static player:
 *  - Analytics: opens, tab switches, per-clip progress milestones
 *    (25/50/75/100 %) and end-of-tour CTA clicks are fire-and-forget
 *    posted to `/api/analytics/event`.
 *  - End-of-tour CTA overlay with smart routing: signed-in users are
 *    invited back to the dashboard, anonymous visitors land on the
 *    signup section of the landing page.
 *  - Static first-frame thumbnails render in the tab-switcher so the
 *    modal shows what's inside each clip before playback starts.
 */
const CLIPS = [
  { key: "cv",        labelKey: "tourClipCv",        Icon: FileText,       src: "/tours/cv_builder_nl.mp4",  thumb: "/tours/cv_builder_nl_thumb.jpg",  ready: true },
  { key: "letter",    labelKey: "tourClipLetter",    Icon: Mail,           src: "/tours/letter_nl.mp4",      thumb: "/tours/letter_nl_thumb.jpg",      ready: true },
  { key: "interview", labelKey: "tourClipInterview", Icon: MessageCircle,  src: "/tours/interview_nl.mp4",   thumb: "/tours/interview_nl_thumb.jpg",   ready: true },
  { key: "radar",     labelKey: "tourClipRadar",     Icon: Radar,          src: "/tours/match_radar_nl.mp4", thumb: "/tours/match_radar_nl_thumb.jpg", ready: true },
];

// Milestone thresholds we report progress at. Each is reported at most once
// per clip play so we can build a funnel per tab without over-counting.
const MILESTONES = [25, 50, 75, 100];

export default function ProductTourModal({ open, onClose, initialClip = "cv", source = "menu" }) {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSignedIn = !!user;

  const [active, setActive] = useState(initialClip);
  const [ended, setEnded] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const videoRef = useRef(null);
  const reportedRef = useRef(new Set()); // "clipKey:milestone" strings

  // Reset per-open so replay/close/reopen restart the funnel cleanly.
  useEffect(() => {
    if (!open) return;
    setActive(initialClip);
    setEnded(false);
    reportedRef.current = new Set();
    trackTourOpened(source);
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, initialClip, onClose, source]);

  const current = useMemo(() => CLIPS.find((c) => c.key === active) || CLIPS[0], [active]);

  const switchClip = (key) => {
    if (key === active) return;
    setActive(key);
    setEnded(false);
    setShareCopied(false);
    trackTourTabSwitched(key);
  };

  // Public share URL — points at the backend OG share page so WhatsApp,
  // LinkedIn, Slack et al. unfurl a rich preview card. The share page in
  // turn meta-refreshes real visitors to `/?tour=<clip>` which the SPA
  // picks up on mount and auto-opens the modal on the requested clip.
  const shareUrl = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/api/share/tour/${encodeURIComponent(active)}`;
  }, [active]);

  const handleCopyShare = async () => {
    // Try the modern Clipboard API first, then fall back to a legacy
    // textarea+execCommand copy so the button still works on browsers
    // where clipboard permission is blocked (older Firefox, insecure
    // contexts, some corporate networks). We flip the visual confirm
    // regardless of the promise result because the user tapped and the
    // link is visible in the readonly input right next to the button.
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
        ok = true;
      } else {
        throw new Error("clipboard-api-unavailable");
      }
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { /* both paths failed — still show the tick as intent */ }
    }
    // Whether or not the write succeeded, flip the state so the user
    // gets feedback and can manually copy the visible URL if needed.
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 1800);
    return ok;
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration || !isFinite(v.duration)) return;
    const pct = (v.currentTime / v.duration) * 100;
    for (const m of MILESTONES) {
      const tag = `${current.key}:${m}`;
      if (pct >= m && !reportedRef.current.has(tag)) {
        reportedRef.current.add(tag);
        trackTourClipProgress(current.key, m);
      }
    }
  };

  const handleEnded = () => {
    // Guarantee 100% milestone is reported even if timeupdate skipped it.
    const tag = `${current.key}:100`;
    if (!reportedRef.current.has(tag)) {
      reportedRef.current.add(tag);
      trackTourClipProgress(current.key, 100);
    }
    // Only fire tour_completed once per clip within a single modal session
    // so replay-then-end sequences don't inflate the completion count.
    const doneTag = `${current.key}:completed`;
    if (!reportedRef.current.has(doneTag)) {
      reportedRef.current.add(doneTag);
      trackTourCompleted(current.key);
    }
    setEnded(true);
  };

  const handleReplay = () => {
    setEnded(false);
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  };

  const handleCta = () => {
    if (isSignedIn) {
      trackTourCtaClick("dashboard");
      onClose();
      navigate("/dashboard");
      return;
    }
    trackTourCtaClick("signup");
    onClose();
    // On the landing page the auth form lives at #signup; when the modal is
    // opened from another route (e.g. /pricing) the router pushes there and
    // the browser scrolls to the anchor automatically.
    if (window.location.pathname === "/") {
      const el = document.getElementById("signup");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
    navigate("/#signup");
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-neutral-900/90"
      onClick={onClose}
      data-testid="product-tour-modal"
    >
      <div
        className="relative w-full max-w-4xl bg-[#FAF7F1] border border-neutral-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-neutral-200">
          <div>
            <p className="ed-eyebrow">{t("tourEyebrow")}</p>
            <h2 className="font-serif text-2xl mt-1">{t(current.labelKey)}</h2>
          </div>
          <button
            data-testid="tour-close"
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 transition-colors"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="grid md:grid-cols-[240px_1fr]">
          <ul className="border-b md:border-b-0 md:border-r border-neutral-200 p-2 flex md:block gap-2 md:gap-1 overflow-x-auto">
            {CLIPS.map((c) => {
              const isActive = active === c.key;
              return (
                <li key={c.key}>
                  <button
                    data-testid={`tour-tab-${c.key}`}
                    onClick={() => c.ready && switchClip(c.key)}
                    disabled={!c.ready}
                    className={`w-full flex items-center gap-2 p-1.5 text-left font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${
                      isActive ? "bg-neutral-900 text-[#F9F8F6]" : "hover:bg-neutral-100"
                    } ${!c.ready ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span
                      data-testid={`tour-thumb-${c.key}`}
                      className={`relative shrink-0 w-14 h-9 overflow-hidden border ${
                        isActive ? "border-amber-300" : "border-neutral-300"
                      }`}
                    >
                      {c.thumb ? (
                        <img
                          src={c.thumb}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="absolute inset-0 bg-neutral-800" />
                      )}
                      <span className={`absolute inset-0 flex items-center justify-center ${
                        isActive ? "bg-black/20" : "bg-black/40"
                      }`}>
                        <c.Icon size={12} strokeWidth={1.75} className="text-[#F9F8F6]" />
                      </span>
                    </span>
                    <span className="flex flex-col min-w-0">
                      <span className="truncate">{t(c.labelKey)}</span>
                      {!c.ready && (
                        <span className="text-[0.55rem] text-amber-700">
                          {t("tourSoon")}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="bg-black relative">
            {current.ready ? (
              <video
                ref={videoRef}
                key={current.key}
                data-testid={`tour-video-${current.key}`}
                src={current.src}
                controls
                autoPlay
                playsInline
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
                onPlay={() => setEnded(false)}
                className="w-full aspect-[128/83] bg-black"
              />
            ) : (
              <div className="aspect-[128/83] flex items-center justify-center text-neutral-400 text-sm font-mono uppercase tracking-[0.15em]">
                {t("tourClipComingSoon")}
              </div>
            )}

            {ended && current.ready && (
              <div
                data-testid="tour-end-overlay"
                className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-neutral-900/85 backdrop-blur-sm px-6 text-center"
              >
                <p className="ed-eyebrow text-amber-300">{t("tourEyebrow")}</p>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#F9F8F6] max-w-md">
                  {t("tourEndTitle")}
                </h3>
                <p className="text-sm text-neutral-300 max-w-sm">
                  {t("tourEndBody")}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                  <button
                    data-testid="tour-end-cta"
                    onClick={handleCta}
                    className="ed-btn ed-btn-primary inline-flex items-center gap-2"
                  >
                    {isSignedIn ? <LayoutDashboard size={13} strokeWidth={1.75} /> : <ArrowRight size={13} strokeWidth={1.75} />}
                    {isSignedIn ? t("tourGoDashCta") : t("tourStartFreeCta")}
                  </button>
                  <button
                    data-testid="tour-end-replay"
                    onClick={handleReplay}
                    className="inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-[#F9F8F6] border border-[#F9F8F6]/40 hover:border-[#F9F8F6] px-3 py-2 transition-colors"
                  >
                    <RotateCcw size={12} strokeWidth={1.75} />
                    {t("tourReplayCta")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-neutral-200 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-neutral-500 font-mono uppercase tracking-[0.15em] shrink-0">
            {t("tourFooterHint")}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="flex items-center gap-1.5 border border-neutral-300 bg-white pl-2 pr-1 py-1"
              data-testid="tour-share-block"
              title={t("tourShareHint")}
            >
              <Share2 size={11} strokeWidth={1.75} className="text-neutral-500 shrink-0" />
              <input
                type="text"
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                data-testid="tour-share-url"
                className="w-40 sm:w-56 bg-transparent font-mono text-[0.65rem] tracking-tight text-neutral-700 outline-none"
              />
              <button
                type="button"
                onClick={handleCopyShare}
                data-testid="tour-share-copy"
                className="font-mono text-[0.6rem] uppercase tracking-[0.15em] px-2 py-1 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 transition-colors inline-flex items-center gap-1"
                aria-label={t("tourShareCopy")}
              >
                {shareCopied ? (
                  <>
                    <Check size={11} strokeWidth={2} />
                    {t("tourShareCopied")}
                  </>
                ) : (
                  <>
                    <Copy size={11} strokeWidth={1.75} />
                    {t("tourShareCopy")}
                  </>
                )}
              </button>
            </div>
            <button
              data-testid="tour-close-cta"
              onClick={onClose}
              className="ed-btn ed-btn-primary inline-flex items-center gap-2"
            >
              <PlayCircle size={13} strokeWidth={1.75} />
              {t("tourCloseCta")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
